// Photos: the full-size image goes to the server; a small thumbnail travels
// with sync so the diary works offline and on every device. If the upload
// fails (offline, weak signal), the full image waits on this device and goes
// up as soon as it can — a medium copy shows meanwhile.

import { ageNow } from '../domain/age.ts';
import { bestPhotoDate, exifTakenAt } from '../domain/exif.ts';
import type { Photo } from '../domain/types.ts';
import { currentPerson, logEvent } from './actions.ts';
import { apiUrl } from './api.ts';
import { devicePrefs } from './device.ts';
import { uid } from './ids.ts';
import { data } from './index.ts';

async function resize(file: Blob, maxEdge: number): Promise<HTMLCanvasElement> {
  const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
  const scale = Math.min(1, maxEdge / Math.max(bmp.width, bmp.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bmp.width * scale);
  canvas.height = Math.round(bmp.height * scale);
  canvas.getContext('2d')!.drawImage(bmp, 0, 0, canvas.width, canvas.height);
  bmp.close();
  return canvas;
}

const toBlob = (c: HTMLCanvasElement, q: number) => new Promise<Blob>((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error('encode failed'))), 'image/jpeg', q));

/** A fingerprint of the file, so the same photo added twice (or shared again) isn't duplicated. */
async function fingerprint(file: Blob): Promise<string | undefined> {
  try {
    const head = await file.slice(0, 512 * 1024).arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', head);
    return `${file.size}:${[...new Uint8Array(digest).slice(0, 12)].map((b) => b.toString(16).padStart(2, '0')).join('')}`;
  } catch {
    return undefined;
  }
}

async function upload(id: string, body: Blob): Promise<string | undefined> {
  const d = devicePrefs();
  if (!d.key) return undefined;
  try {
    const res = await fetch(apiUrl(`/api/photos/${id}`), { method: 'PUT', headers: { 'Content-Type': 'image/jpeg', Authorization: `Bearer ${d.key}` }, body, signal: AbortSignal.timeout(60_000) });
    if (!res.ok) return undefined;
    return ((await res.json().catch(() => ({}))) as { token?: string }).token ?? '';
  } catch {
    return undefined;
  }
}

const QUEUE = 'photo-queue';

/** Keep the full image on this device until it reaches the server. */
async function queueUpload(id: string, blob: Blob): Promise<void> {
  const db = data().db;
  await db.setMeta(`photo-upload:${id}`, blob);
  const q = (await db.getMeta<string[]>(QUEUE)) ?? [];
  if (!q.includes(id)) await db.setMeta(QUEUE, [...q, id]);
}

let retrying = false;
/** Send any photos that couldn't be uploaded earlier. Safe to call often. */
export async function retryPhotoUploads(): Promise<number> {
  if (retrying || !devicePrefs().key) return 0;
  retrying = true;
  let sent = 0;
  try {
    const db = data().db;
    const q = (await db.getMeta<string[]>(QUEUE)) ?? [];
    const left: string[] = [];
    for (const id of q) {
      const blob = await db.getMeta<Blob>(`photo-upload:${id}`);
      const photo = data().store.get('photos', id)?.data as Photo | undefined;
      if (!blob || !photo) {
        await db.setMeta(`photo-upload:${id}`, null);
        continue;
      }
      const token = await upload(id, blob);
      if (token === undefined) {
        left.push(id);
        continue;
      }
      const thumb = await resize(blob, 360);
      data().store.put('photos', { ...photo, full: true, token: token || undefined, data: thumb.toDataURL('image/jpeg', 0.72) });
      await db.setMeta(`photo-upload:${id}`, null);
      sent++;
    }
    await db.setMeta(QUEUE, left);
  } finally {
    retrying = false;
  }
  return sent;
}

/** How many photos are still waiting to reach the server from this device. */
export async function pendingUploads(): Promise<number> {
  return ((await data().db.getMeta<string[]>(QUEUE)) ?? []).length;
}

export async function addPhoto(file: Blob, opts: { weekly?: boolean; caption?: string; at?: number; googleId?: string; hash?: string } = {}): Promise<Photo> {
  const id = uid();
  const at = opts.at ?? Date.now();
  const [full, thumb] = await Promise.all([resize(file, 1600), resize(file, 360)]);
  const fullBlob = await toBlob(full, 0.85);
  const token = await upload(id, fullBlob);
  const uploaded = token !== undefined;
  if (!uploaded) await queueUpload(id, fullBlob).catch(() => undefined);
  const small = uploaded ? thumb.toDataURL('image/jpeg', 0.72) : (await resize(file, 900)).toDataURL('image/jpeg', 0.8);
  const profile = data().store.get('profile', 'arlo');
  const photo: Photo = {
    id,
    at,
    data: small,
    full: uploaded || undefined,
    token: token || undefined,
    weekly: opts.weekly || undefined,
    ageWeeks: profile ? ageNow(profile, at)?.weeks : undefined,
    caption: opts.caption?.trim() || undefined,
    by: currentPerson(),
    googleId: opts.googleId,
    hash: opts.hash,
  };
  data().store.put('photos', photo);
  logEvent('photo', { photoId: id }, { at });
  return photo;
}

export interface ImportResult {
  added: number;
  duplicates: number;
  failed: number;
  queued: number;
}

/**
 * Add many photos at once (from the gallery, or shared from another app):
 * each keeps the date it was taken, and ones already in the diary are skipped.
 */
export async function importPhotos(files: Blob[], onProgress?: (done: number, total: number) => void): Promise<ImportResult> {
  const result: ImportResult = { added: 0, duplicates: 0, failed: 0, queued: 0 };
  const have = new Set(data().store.list('photos').map((p) => p.hash).filter(Boolean));
  const profile = data().store.get('profile', 'arlo');
  const born = profile?.dob ? new Date(`${profile.dob}T00:00`).getTime() : undefined;
  let done = 0;
  for (const file of files) {
    try {
      const hash = await fingerprint(file);
      if (hash && have.has(hash)) {
        result.duplicates++;
      } else {
        const head = await file.slice(0, 128 * 1024).arrayBuffer();
        const taken = exifTakenAt(head);
        const lastModified = 'lastModified' in file ? (file as File).lastModified : undefined;
        const p = await addPhoto(file, { at: bestPhotoDate(taken, lastModified, Date.now(), born), hash });
        if (hash) have.add(hash);
        result.added++;
        if (!p.full) result.queued++;
      }
    } catch {
      result.failed++;
    }
    onProgress?.(++done, files.length);
  }
  return result;
}

/** "12 photos added · 2 already in the diary · 3 will upload when you're back online". */
export function describeImport(r: ImportResult): string {
  const parts = [r.added === 1 ? '1 photo added' : `${r.added} photos added`];
  if (r.duplicates) parts.push(`${r.duplicates} already in the diary`);
  if (r.failed) parts.push(`${r.failed} couldn’t be read`);
  if (r.queued) parts.push(`${r.queued} will finish uploading when the signal’s back`);
  return parts.join(' · ');
}

export function updatePhoto(p: Photo): void {
  data().store.put('photos', p);
}

export function deletePhoto(p: Photo): void {
  data().store.remove('photos', p.id);
  for (const e of data().store.list('events')) if (e.type === 'photo' && e.data.photoId === p.id) data().store.remove('events', e.id);
  const d = devicePrefs();
  if (p.full && d.key) void fetch(apiUrl(`/api/photos/${p.id}`), { method: 'DELETE', headers: { Authorization: `Bearer ${d.key}` } }).catch(() => undefined);
}

/** Full-size image URL, via a per-photo token (falls back to the synced copy). */
export function photoSrc(p: Photo): string {
  return p.full && p.token ? apiUrl(`/api/photos/${p.id}?t=${encodeURIComponent(p.token)}`) : p.data;
}

/**
 * Photos shared to Arlo HQ from the phone's gallery: the service worker
 * parks them in a cache (see sw.js) and opens #/diary?shared=<batch>. Take
 * them out — once — as files with their original dates.
 */
export async function takeShared(batch: string): Promise<File[]> {
  if (!('caches' in self) || !/^[\w-]+$/.test(batch)) return [];
  const cache = await caches.open('arlo-shared');
  const keys = (await cache.keys()).filter((r) => new URL(r.url).pathname.includes(`/shared/${batch}/`));
  const files: File[] = [];
  for (const k of keys) {
    const res = await cache.match(k);
    if (res) {
      const blob = await res.blob();
      const lastModified = Number(res.headers.get('X-Last-Modified')) || Date.now();
      files.push(new File([blob], k.url.split('/').pop() ?? 'shared.jpg', { type: blob.type || 'image/jpeg', lastModified }));
    }
    await cache.delete(k);
  }
  return files;
}
